import PageHeader from "@/components/pageHeader";
import { ArrowRight, ChevronLeft,ChevronRight, Play } from "lucide-react";
import { useState } from "react";

export const CSRPage = () => {
  // Sourced from gillcogroup.com/csr
  const featured = {
    title: "Gillco Stands with Punjab: Together in Strength and Solidarity",
    date: "August 2025",
    excerpt: "When Punjab needed us, we stood together. A heartfelt initiative reaffirming Gillco's commitment to the land that has nurtured our story for over two decades.",
    image: "/assets/culture/csr/gillco-stand-with-punjab2.jpeg",
    link: "https://www.youtube.com/watch?v=3-5jh6JGSUA",
    tag: "Latest",
  };

  const initiatives = [
    {
      title: "Langar Service on the occasion of Gurupurab",
      date: "June 2022",
      excerpt: "On the auspicious Guruparab of Sri Guru Hargobind Singh, Gillco Group organised a Langar service — a humble offering to the community and a tribute to Sikh values.",
      image: "/assets/culture/csr/langar-service.jpg",
      link: "https://m.facebook.com/gillcogroupofficial/posts/1747949502216562/",
      tag: "Community",
    },
    {
      title: "Run for Health Marathon",
      date: "February 2023",
      excerpt: "Tricity's biggest 'Run for Health' marathon powered by Gillco Group — bringing together thousands across age groups to celebrate fitness and wellness.",
      image: "/assets/culture/csr/health-marathon2023.png",
      link: "https://www.facebook.com/gillcogroupofficial/videos/857669398637716/",
      tag: "Health",
    },
    {
      title: "Run for Health Marathon",
      date: "January 2020",
      excerpt: "The legacy edition of Gillco's flagship 'Run for Health' marathon — a moment that solidified Gillco's commitment to public wellness in the Tricity region.",
      image: "/assets/culture/csr/health-marathon2020.jpg",
      link: "https://www.facebook.com/share/p/15cxxdBqwi/",
      tag: "Health",
    },
    {
      title: "Blood Donation Camp at Gillco Park Hills",
      date: "November 2021",
      excerpt: "Gillco Park Hills hosted a blood donation camp, mobilising residents and the wider community to contribute to a life-saving cause.",
      image: "/assets/culture/csr/blood-donation-camp.jpg",
      link: "https://www.facebook.com/story.php?story_fbid=1604010966610417&id=192323624445832",
      tag: "Health",
    },
    {
      title: "COVID Vaccination Drive",
      date: "May 2020",
      excerpt: "During the pandemic's hardest months, Gillco Group organised a vaccination drive — protecting our communities and supporting frontline efforts.",
      image: "/assets/culture/csr/vaccination-drive.jpg",
      link: "https://www.facebook.com/story.php?story_fbid=1456155744729274&id=192323624445832",
      tag: "Health",
    },
  ];

  return (
    <div data-testid="v13-csr-page">
      <PageHeader title="CSR Initiatives" subtitle="Giving Back" backgroundImage="/assets/banner/csr-banner.avif" />

      {/* Editorial hero — matches Journey / Spotlight style */}
      <section className="bg-white pt-24 lg:pt-32 pb-16 lg:pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex gap-8 lg:gap-16">
            <div className="hidden md:flex flex-col items-center">
              <span
                className="text-[11px] tracking-[0.3em] uppercase text-[#C4A265]"
                style={{ writingMode: "vertical-lr", letterSpacing: "0.3em" }}
              >
                CSR ACTIVITIES
              </span>
              <div className="w-[1px] bg-[#C4A265] flex-1 mt-4 min-h-[120px]" />
            </div>
            <div className="flex-1">
              <h2
                className="text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] mb-10 lg:mb-12 leading-[1.05] relative inline-block pr-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Giving Back<br />What&rsquo;s Yours
                <span className="absolute bottom-3 -right-2 w-2 h-2 rounded-full bg-[#C4A265]" />
              </h2>
              <p className="text-[#4A4A4A] leading-relaxed max-w-3xl" style={{ textAlign: "justify" }}>
                We are deeply supportive of our communities. We never forget to walk the talk and initiate the right cause for the people who have shown trust and confidence in us over the last two decades. From healthcare drives and education to celebrations of culture and faith, our initiatives are a reflection of the values we live by.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Latest from Gillco — featured CSR story */}
      <section className="bg-[#f8f6f2] py-20 lg:py-24 border-y border-[#C4A265]/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#C4A265] mb-8 font-medium">The Latest from Gillco</div>
          <a
            href={featured.link}
            target="_blank"
            rel="noopener noreferrer"
            className="group grid lg:grid-cols-12 gap-10 lg:gap-14 items-center"
            data-testid="v13-csr-featured"
          >
            <div className="lg:col-span-7 overflow-hidden bg-[#ece6d8]">
              <div className="aspect-[4/4] overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-1000"
                />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#0A4F3B] bg-white px-3 py-1 border border-[#0A4F3B]/20">{featured.tag}</span>
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#C4A265]">{featured.date}</span>
              </div>
              <h3
                className="text-3xl lg:text-5xl text-[#0A0A0A] font-light leading-[1.1] mb-5 group-hover:text-[#0A4F3B] transition-colors duration-500"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {featured.title}
              </h3>
              <div className="w-14 h-[2px] bg-[#C4A265] mb-5 group-hover:w-24 transition-all duration-500" />
              <p className="text-[15px] text-[#4A4A4A] leading-[1.85] mb-6" style={{ textAlign: "justify" }}>
                {featured.excerpt}
              </p>
              <span className="inline-flex items-center gap-2 text-[#0A4F3B] text-sm font-medium group-hover:gap-3 transition-all">
                <Play size={14} className="text-[#C4A265]" /> Watch the Story
              </span>
            </div>
          </a>
        </div>
      </section>

      {/* Our Initiatives — grid */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12 lg:mb-16">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#C4A265] mb-3 font-medium">Our Initiatives</div>
              <h3
                className="text-3xl lg:text-4xl text-[#0A0A0A] font-light leading-tight mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Communities, Causes &amp; Care
              </h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed" style={{ textAlign: "justify" }}>
                As one of Punjab&rsquo;s most trusted real estate developers, we actively organise and participate in community-driven events, cultural celebrations and meaningful initiatives that foster connection, inclusivity and positive change.
              </p>
            </div>
            <div className="hidden lg:block w-24 h-[1px] bg-[#C4A265]/40 mb-3" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-9">
            {initiatives.map((m, i) => (
              <a
                key={i}
                href={m.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col bg-white border border-[#C4A265]/15 hover:border-[#C4A265] transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(196,162,101,0.25)] overflow-hidden"
                data-testid={`v13-csr-card-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#f8f6f2]">
                  <img
                    src={m.image}
                    alt={m.title}
                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                  />
                </div>
                <div className="p-6 lg:p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[9px] uppercase tracking-[0.3em] text-[#0A4F3B] bg-[#0A4F3B]/8 px-2 py-1 border border-[#0A4F3B]/15">{m.tag}</span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#C4A265]">{m.date}</span>
                  </div>
                  <h4
                    className="text-xl lg:text-2xl text-[#0A0A0A] font-light leading-[1.2] mb-3 group-hover:text-[#0A4F3B] transition-colors duration-500"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {m.title}
                  </h4>
                  <div className="w-10 h-[1px] bg-[#C4A265] mb-4 group-hover:w-16 transition-all duration-500" />
                  <p className="text-sm text-[#4A4A4A] leading-relaxed mb-5 flex-1" style={{ textAlign: "justify" }}>
                    {m.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#0A4F3B] text-sm font-medium group-hover:gap-3 transition-all self-start">
                    Explore <ArrowRight size={14} />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};